import { useNavigate } from "react-router";
import { skipToken, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { type PageOptions, usePage } from "@/api/pagination";
import ModelsService from "@/api/services/models";
import { useToast } from "@/providers/ToastProvider";
import { ExistingModel, ExistingRecipient } from "@/types";

// Not sorted: a page is whatever the server returned for that cursor. Sorting by name only
// makes sense across the whole set, which is what useAllModels does.
export function useModels(params: PageOptions = {}, filters: Record<string, string> = {}) {
  return usePage<ExistingModel>(
    ["models", filters],
    (paginationParams) => ModelsService.getModels(paginationParams, filters),
    params,
  );
}

export function useModelsForRecipient(recipientId?: ExistingRecipient["id"]) {
  return useQuery({
    queryKey: ["models", "recipient", recipientId],
    queryFn: recipientId ? () => ModelsService.getModelsForRecipient(recipientId) : skipToken,
    // maintain previous behavior: sort by model name. No expected interaction with pagination since the service returns all models.
    select: (data) => data.sort((a, b) => a.model_name.localeCompare(b.model_name)),
  });
}

export function useAllModels() {
  return useQuery({
    queryKey: ["models", "all"],
    // maintain previous behavior: sort by model name
    select: (data) => data.sort((a, b) => a.model_name.localeCompare(b.model_name)),
    queryFn: () => ModelsService.getAllModels(),
  });
}

export function useModel(modelId: ExistingModel["id"]) {
  return useQuery({
    queryKey: ["models", modelId],
    queryFn: () => ModelsService.getModel(modelId),
  });
}

export function useValidateModel() {
  const { addToast } = useToast();
  return useMutation({
    mutationFn: (model: ExistingModel) => ModelsService.validateModel(model),
    onSuccess: () => {
      addToast({
        kind: "SUCCESS",
        message: `The model configuration is valid, and its tables and columns exist in the source database.`,
      });
    },
    onError: (error) => {
      addToast({
        kind: "ERROR",
        message: error.message,
      });
    },
  });
}

export function useDeleteModel() {
  const queryClient = useQueryClient();
  const { addToast } = useToast();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (payload: { id: string; redirectPath?: string }) => ModelsService.deleteModel(payload.id),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["models"] });
      if (variables.redirectPath) {
        navigate(variables.redirectPath);
      }
      addToast({
        kind: "SUCCESS",
        message: `Model deleted successfully!`,
      });
    },
  });
}
